'use client';
import React from 'react';
import { Container, Section, SectionGroup } from '@layout';
import { Tabs, Card, Button, Heading } from '@ui';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import { withOptionalId } from '@lib/withOptionalId';

interface AboutWrapperProps {
  sectionID?: string;
  className?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

type PrincipleLine = { title: string; body: string };

type DesignSystemSection = {
  eyebrow: string;
  headline: string;
  intro: string;
  includes_heading: string;
  includes_bullets: string[];
  matters_heading: string;
  matters_body: string;
  principles_heading: string;
  principles: PrincipleLine[];
  cta_heading: string;
  cta_body: string;
  cta_label: string;
};

type AboutSection = {
  title: string;
  title_short?: string;
  body: string[] | string;
  design_system?: DesignSystemSection;
};

function isPrincipleLine(x: unknown): x is PrincipleLine {
  if (!x || typeof x !== 'object') return false;
  const o = x as { title?: unknown; body?: unknown };
  return typeof o.title === 'string' && typeof o.body === 'string';
}

function parseDesignSystem(raw: unknown): DesignSystemSection | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  const principlesRaw = d.principles;
  const principles = Array.isArray(principlesRaw) ? principlesRaw.filter(isPrincipleLine) : [];
  if (
    typeof d.eyebrow !== 'string' ||
    typeof d.headline !== 'string' ||
    typeof d.intro !== 'string' ||
    typeof d.includes_heading !== 'string' ||
    !Array.isArray(d.includes_bullets) ||
    typeof d.matters_heading !== 'string' ||
    typeof d.matters_body !== 'string' ||
    typeof d.principles_heading !== 'string' ||
    typeof d.cta_heading !== 'string' ||
    typeof d.cta_body !== 'string' ||
    typeof d.cta_label !== 'string'
  ) {
    return undefined;
  }
  const bullets = d.includes_bullets.filter((b): b is string => typeof b === 'string');
  return {
    eyebrow: d.eyebrow,
    headline: d.headline,
    intro: d.intro,
    includes_heading: d.includes_heading,
    includes_bullets: bullets,
    matters_heading: d.matters_heading,
    matters_body: d.matters_body,
    principles_heading: d.principles_heading,
    principles,
    cta_heading: d.cta_heading,
    cta_body: d.cta_body,
    cta_label: d.cta_label,
  };
}

function isAboutSection(x: unknown): x is AboutSection {
  return Boolean(x && typeof x === 'object' && 'title' in x);
}

const AboutWrapper: React.FC<AboutWrapperProps> = ({
  sectionID,
  className,
  classes = '',
  containerId,
  containerClasses,
  isSpanish = false,
}) => {
  const locale = isSpanish ? 'es' : 'en';
  const headline = renderTranslation(t('headline', 'about', locale));
  const sectionsRaw = t('sections', 'about', locale);
  const sections: AboutSection[] = Array.isArray(sectionsRaw)
    ? sectionsRaw.filter(isAboutSection)
    : [];

  const designSystemHref = locale === 'es' ? '/es/design-system' : '/design-system';

  const renderDesignSystemTab = (ds: DesignSystemSection) => (
    <div className='about-design-system'>
      <div className='preface'>
        <p className='eyebrow'>{renderTranslation(ds.eyebrow)}</p>
        <Heading as='h2' id='design-system-tab-title' className='headline'>
          {renderTranslation(ds.headline)}
        </Heading>
        <p className='sub full lead'>{renderTranslation(ds.intro)}</p>
      </div>

      <div className='grid-2'>
        <Card className='surface-1 feature'>
          <Heading as='h3' className='block-title'>
            {renderTranslation(ds.includes_heading)}
          </Heading>
          <ul className='list'>
            {ds.includes_bullets.map((line) => (
              <li key={line}>{renderTranslation(line)}</li>
            ))}
          </ul>
        </Card>
        <Card className='surface-1 feature'>
          <Heading as='h3' className='block-title'>
            {renderTranslation(ds.matters_heading)}
          </Heading>
          <p className='sub full matters'>{renderTranslation(ds.matters_body)}</p>
        </Card>
      </div>

      <Card className='surface-2 standards'>
        <Heading as='h3' className='panel-title'>
          {renderTranslation(ds.principles_heading)}
        </Heading>
        <div className='grid-2 matrix'>
          {ds.principles.map((p) => (
            <div key={p.title} className='point'>
              <p className='title'>{renderTranslation(p.title)}</p>
              <p className='small body'>{renderTranslation(p.body)}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className='surface-1 closing'>
        <div className='inner'>
          <div className='copy'>
            <Heading as='h3' className='title'>
              {renderTranslation(ds.cta_heading)}
            </Heading>
            <p className='sub full body'>{renderTranslation(ds.cta_body)}</p>
          </div>
          <Button
            as='a'
            href={designSystemHref}
            label={String(renderTranslation(ds.cta_label))}
            className='btn-primary action'
          />
        </div>
      </Card>
    </div>
  );

  return (
    <SectionGroup
      groupId={sectionID || 'about-wrapper'}
      groupClassName='section-group'
      title={String(headline)}
      description={String(renderTranslation(t('headline-subtitle', 'about', locale)))}
      headerClassName='flush'
      as='h1'
      headingClassName='slender'
    >
      <Section {...withOptionalId(sectionID)} className={className || classes}>
        <Container containerId={containerId} containerClasses={containerClasses}>
          <Tabs
            tabs={sections.map((section, idx) => {
              const parsedDs = section.design_system
                ? parseDesignSystem(section.design_system)
                : undefined;
              return {
                id: `about-tab-${idx}`,
                label: String(renderTranslation(section.title_short ?? section.title)),
                panelId: `about-panel-${idx}`,
                title: parsedDs ? '' : String(renderTranslation(section.title)),
                content: parsedDs
                  ? renderDesignSystemTab(parsedDs)
                  : Array.isArray(section.body)
                    ? section.body.map((para, pIdx) => <p key={pIdx}>{renderTranslation(para)}</p>)
                    : renderTranslation(section.body),
              };
            })}
            id='about-tab'
            className=''
            persistKey='about-active-tab'
          />
        </Container>
      </Section>
    </SectionGroup>
  );
};

export default AboutWrapper;
